import React from 'react';
import { Alert } from 'react-bootstrap';

export default function ContentModerationPolicy() {
    return (
        <div className="content-moderation-policy">
            <p className="text-muted mb-4">
                <em>Last Updated: March 2026</em>
            </p>

            <p>
                Sustainability Hub uses a combination of automated tools,
                community moderators, and platform staff to review content and
                maintain a safe, respectful environment. This policy explains
                how that system works, what happens when content is removed, and
                how you can appeal a moderation decision.
            </p>

            {/* ── Automated Moderation ── */}
            <h5 className="mt-4 mb-3 fw-bold">Automated Moderation</h5>
            <p>
                Every post submitted on Sustainability Hub is automatically
                reviewed before it is published. When you submit a post, our
                automated system checks the content in real time. There are two
                possible outcomes:
            </p>
            <ul className="ps-4">
                <li>
                    <strong>Approved:</strong> The post passes the automated
                    review and is published immediately.
                </li>
                <li>
                    <strong>Blocked for review:</strong> The post is held and
                    not published. You will be notified with a reason explaining
                    why your post did not go through. A human moderator will
                    then review the content before any final action is taken.
                </li>
            </ul>
            <Alert variant="info" className="mt-3">
                <strong>Disclosure:</strong> Automated tools assist in
                moderation decisions but do not make final determinations
                independently. All blocked posts are reviewed by a human
                moderator before any action is taken.
            </Alert>

            {/* ── Bili AI ── */}
            <h5 className="mt-4 mb-3 fw-bold">
                Bili AI Assistant &amp; Data Processing
            </h5>
            <p>
                The Bili AI Assistant processes the messages and queries you
                submit to it in order to generate responses. Please be aware:
            </p>
            <ul className="ps-4">
                <li>
                    Conversations with Bili are processed by AI systems and may
                    be reviewed by our team to improve the service and ensure
                    safety
                </li>
                <li>
                    Do not share sensitive personal information, passwords, or
                    confidential data in your Bili conversations
                </li>
                <li>
                    Bili interactions are subject to our{' '}
                    <strong>Acceptable Use Policy</strong> and may be
                    rate-limited or restricted if misuse is detected
                </li>
            </ul>

            {/* ── Community Moderators ── */}
            <h5 className="mt-4 mb-3 fw-bold">Community Moderators</h5>
            <p>
                Each community on Sustainability Hub may have designated
                moderators — trusted members assigned by the community creator.
                Moderators can:
            </p>
            <ul className="ps-4">
                <li>
                    Review reported posts and replies within their community
                </li>
                <li>Remove content that violates Community Guidelines</li>
                <li>Pin important posts</li>
                <li>
                    Ban members from the community for repeated or serious
                    violations
                </li>
            </ul>
            <p>
                Moderators are expected to act in good faith and in accordance
                with our Community Guidelines. Moderator actions are subject to
                oversight by platform staff.
            </p>

            {/* ── Notice to Users ── */}
            <h5 className="mt-4 mb-3 fw-bold">
                Notice When Content Is Removed
            </h5>
            <p>
                If your content is removed by a moderator or platform staff, we
                will notify you with:
            </p>
            <ul className="ps-4">
                <li>
                    The reason for removal (e.g., harassment, spam, guideline
                    violation)
                </li>
                <li>
                    A reference to the specific Community Guideline or policy
                    that was violated
                </li>
                <li>Information on how to appeal the decision</li>
            </ul>
            <p>
                In cases of urgent safety concerns (e.g., imminent harm, illegal
                content), content may be removed immediately without prior
                notice. You will still be notified afterward.
            </p>

            {/* ── Appeals ── */}
            <h5 className="mt-4 mb-3 fw-bold">
                Appealing a Moderation Decision
            </h5>
            <p>
                If you believe your content was removed in error, you have the
                right to appeal. To submit an appeal:
            </p>
            <ol className="ps-4">
                <li>
                    Email{' '}
                    <a href="mailto:support@sustainabilityhub.co">
                        support@sustainabilityhub.co
                    </a>{' '}
                    with the subject line{' '}
                    <strong>&ldquo;Moderation Appeal&rdquo;</strong>
                </li>
                <li>
                    Include the content that was removed and the reason you
                    believe it does not violate our guidelines
                </li>
                <li>
                    We will review your appeal and respond within{' '}
                    <strong>5 business days</strong>
                </li>
            </ol>
            <p>
                Appeals are reviewed by platform staff, not the moderator who
                made the original decision.
            </p>

            {/* ── Escalation ── */}
            <h5 className="mt-4 mb-3 fw-bold">Escalation &amp; Contact</h5>
            <p>
                For questions about moderation decisions or to report moderator
                misconduct, contact us at{' '}
                <a href="mailto:support@sustainabilityhub.co">
                    support@sustainabilityhub.co
                </a>
                .
            </p>
        </div>
    );
}
